import React, { useEffect, useState } from 'react';
import { Divider, Modal, Space, Tag, Typography } from 'antd';

import { getItemAudience } from '../../services/sharing';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import GroupKindTag from '../collections/GroupKindTag';
import UserRef from '../users/UserRef';
import { normalizeId } from '../../utils/ids';
import { COLLECTION_ROLE_OPTIONS } from '../../utils/permissions';

const { Text } = Typography;

// One vocabulary with the Sharing tab ('Can view' / 'Can edit' / 'Manager').
const ROLE_LABELS = new Map(COLLECTION_ROLE_OPTIONS.map(opt => [opt.value, opt.label]));
const roleTag = role => <Tag>{ROLE_LABELS.get(role) || role}</Tag>;

// A SUB-COLLECTION's record is seen by everyone granted on the collections
// above it too (keepr-api § Sub-collections): such an entry names where the
// access comes from — "via Biewend" — beside the role it gives.
export const viaNote = entry => (entry?.inheritedFrom?.name
	? <Text type="secondary" className="audience-modal__via small">{`via ${entry.inheritedFrom.name}`}</Text>
	: null);
const accessTag = entry => (
	<Space size={6} className="audience-modal__access">
		{viaNote(entry)}
		{roleTag(entry.role)}
	</Space>
);

function PersonRow({ user, tag }) {
	return (
		<div className="audience-modal__row">
			<UserRef user={user} />
			{tag}
		</div>
	);
}

function GroupRow({ entry }) {
	return (
		<div className="audience-modal__group">
			<div className="audience-modal__row">
				<Space>
					<Tag>Group</Tag>
					<span>{entry.group?.name || 'Group'}</span>
					{/* A dynamic group's members are whoever its rule matches
					    right now; the chip's hover says so. */}
					<GroupKindTag group={entry.group} />
				</Space>
				{accessTag(entry)}
			</div>
			<div className="audience-modal__group-members" style={{ paddingLeft: 24 }}>
				{(entry.members || []).length === 0
					? <Text type="secondary">{entry.group?.kind === 'dynamic' ? 'No records match the rule right now' : 'No members'}</Text>
					: entry.members.map(member => (
						<PersonRow key={normalizeId(member?._id)} user={member} tag={null} />
					))}
			</div>
		</div>
	);
}

function entryRow(entry, index) {
	return entry.type === 'group'
		? <GroupRow key={`group-${normalizeId(entry.group?._id) || index}`} entry={entry} />
		: (
			<PersonRow
				key={`user-${normalizeId(entry.user?._id) || index}`}
				user={entry.user}
				tag={accessTag(entry)}
			/>
		);
}

/**
 * The on-demand audience audit: who can see or change this record, right
 * now. Server-computed from the same grant/group sources the API enforces
 * with (services/sharing.getItemAudience), so what it lists is what the
 * resolver will actually allow — not a client-side guess.
 */
export default function ItemAudienceModal({ open, onClose, itemId, itemTitle }) {
	const [audience, setAudience] = useState(null);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!open) return undefined;
		// Never show a previous item's audience under a new title: drop stale
		// state the moment the modal (re)opens or the target changes.
		setAudience(null);
		setError(false);
		if (!itemId) {
			// Open with no resolvable id (unsaved item) — report, don't guess.
			setError(true);
			setLoading(false);
			return undefined;
		}
		let active = true;
		setLoading(true);
		getItemAudience(itemId)
			.then(data => { if (active) setAudience(data); })
			.catch(() => { if (active) setError(true); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [open, itemId, attempt]);

	// A row the API marks `untilAccepted` (an unreviewed intake submission's
	// readers, who see it only once it is accepted) is not counted among who
	// can see it now. An API that never sends the mark leaves every row in
	// the first list, exactly as before.
	const entries = audience?.entries || [];
	const now = entries.filter(entry => entry?.untilAccepted !== true);
	const later = entries.filter(entry => entry?.untilAccepted === true);

	return (
		<Modal
			title={itemTitle ? `Who can see “${itemTitle}”` : 'Who can see this item'}
			open={open}
			onCancel={onClose}
			footer={null}
		>
			<DialogLoadState
				loaded={audience !== null}
				loading={loading}
				error={error}
				label="the audience for this item"
				onRetry={itemId ? () => setAttempt(n => n + 1) : undefined}
				skeleton={<Skeleton variant="list-row" count={4} />}
				minHeight={220}
			>
			{audience ? (
				<div className="audience-modal">
					{audience.owner ? (
						<PersonRow user={audience.owner} tag={<Tag color="gold">Owner</Tag>} />
					) : null}

					{now.length ? <Divider plain>Shared with</Divider> : null}
					{now.map(entryRow)}
					{!now.length ? (
						<Text type="secondary">Not shared with anyone else.</Text>
					) : null}

					{/* A submission waiting for review: these rows read it once
					    someone accepts it, and not before (keepr-api KPR-843). */}
					{later.length ? <Divider plain>Sees it once it’s accepted</Divider> : null}
					{later.map(entryRow)}

					{audience.includesAdmins ? (
						<div style={{ marginTop: 12 }}>
							<Text type="secondary" className="small">
								keepr administrators can always access records.
							</Text>
						</div>
					) : null}
				</div>
			) : null}
			</DialogLoadState>
		</Modal>
	);
}
